import { Setting } from "@greco/components/dist/types/types";
import {
  createStore,
  createHook,
  createActionsHook,
  createStateHook,
} from "react-sweet-state";
import { ExpandedColumn } from "types/columns";
import { LoadStatus } from "types/types";
import { columns } from "../pages/ListPage/columns";
import {
  createSetting,
  deleteSetting,
  importToGos,
  invalidateOffer,
  loadOffer,
  loadSettings,
  loadVehicles,
  loadVehiclesForReport,
  seedSettings,
  setStoreValue,
  setStoreValues,
  submitAsiromForm,
  submitBcrForm,
  submitRenewalForm,
  submitRenewalNonGrecoForm,
  updateSetting,
} from "./ListPageStoreActions";

export type StoreState = {
  settings: Setting[];
  settingsApplicationCodeId: number | null;
  offerRequestType: string;
  settingsLoadStatus: LoadStatus;
  invalidateOfferStatus: LoadStatus;
  searchTerm: string;
  isBackOperation: boolean;
  showArchived: boolean;
  vehicleItems: any[];
  vehicleItemsLoadStatus: LoadStatus;
  submitBcrFormStatus: LoadStatus;
  submitAsiromFormStatus: LoadStatus;
  submitRenewalFormStatus: LoadStatus;
  submitRenewalNonGrecoFormStatus: LoadStatus;
  importToGosStatus: LoadStatus;
  offerLoadStatus: LoadStatus;
  sort?: {
    key: string;
    dir: string;
  };
  skip: number;
  top?: number;
  cols: ExpandedColumn[];
  selectedViewColumns: ExpandedColumn[];
  selectedViewId: number;
  selectedView: any;
  tempColumns: ExpandedColumn[];
  count: number;
  invalidateOfferDialog: {
    isOpen: boolean;
    offer: any;
  };
  asiromFormDialog: {
    isOpen: boolean;
    formData: any;
  };
  bcrFormDialog: {
    isOpen: boolean;
    formData: any;
  };
  renewalFormDialog: {
    isOpen: boolean;
    formData: any;
  };
  renewalNonGrecoFormDialog: {
    isOpen: boolean;
    formData: any;
  };
  importToGosDialog: {
    isOpen: boolean;
    offer: any;
  };
  areAnyColumnsFiltered: boolean;
  columnOptionsSettings: Setting[];
  selectedOfferRequestType: any;
  searchState: string;
  debouncedState: string;
};

// This is the value of the store on initialisation
const initialState: StoreState = {
  settings: [],
  settingsApplicationCodeId: null,
  offerRequestType: "",
  settingsLoadStatus: "none",
  invalidateOfferStatus: "none",
  searchTerm: "",
  vehicleItems: [],
  vehicleItemsLoadStatus: "none",
  submitBcrFormStatus: "none",
  submitAsiromFormStatus: "none",
  submitRenewalFormStatus: "none",
  submitRenewalNonGrecoFormStatus: "none",
  importToGosStatus: "none",
  offerLoadStatus: "none",
  sort: {
    key: "",
    dir: "",
  },
  isBackOperation: false,
  showArchived: false,
  skip: 0,
  top: 20,
  cols: columns,
  selectedViewColumns: null,
  selectedViewId: null,
  selectedView: null,
  tempColumns: columns,
  count: 0,
  invalidateOfferDialog: {
    isOpen: false,
    offer: null,
  },
  asiromFormDialog: {
    isOpen: false,
    formData: null,
  },
  bcrFormDialog: {
    isOpen: false,
    formData: null,
  },
  renewalFormDialog: {
    isOpen: false,
    formData: null,
  },
  renewalNonGrecoFormDialog: {
    isOpen: false,
    formData: null,
  },
  importToGosDialog: {
    isOpen: false,
    offer: null,
  },
  areAnyColumnsFiltered: false,
  columnOptionsSettings: [],
  searchState: "",
  debouncedState: "",
  selectedOfferRequestType: null,
};

// All the actions that mutate the store
const actions = {
  setListStoreValue: setStoreValue,
  setListStoreValues: setStoreValues,
  loadOffer: loadOffer,
  loadSettings: loadSettings,
  seedSettings: seedSettings,
  loadVehicles: loadVehicles,
  loadVehiclesForReport: loadVehiclesForReport,
  invalidateOffer: invalidateOffer,
  createSetting: createSetting,
  deleteSetting: deleteSetting,
  updateSetting: updateSetting,
  submitBcrForm: submitBcrForm,
  submitAsiromForm: submitAsiromForm,
  submitRenewalForm: submitRenewalForm,
  submitRenewalNonGrecoForm: submitRenewalNonGrecoForm,
  importToGos: importToGos,
};

export const ListPageStore = createStore({
  initialState,
  actions,
  name: "ListPageStore",
});
export const useListPageStore = createHook(ListPageStore);
export const useListPageStoreState = createStateHook(ListPageStore);
export const useListPageStoreActions = createActionsHook(ListPageStore);

export const useSettings = createHook(ListPageStore, {
  selector: (state) => ({ settings: state.settings }),
});

export const useVehicleItems = createHook(ListPageStore, {
  selector: (state) => ({ vehicleItems: state.vehicleItems }),
});

export const useVehicleItemsLoadStatus = createHook(ListPageStore, {
  selector: (state) => ({
    vehicleItemsLoadStatus: state.vehicleItemsLoadStatus,
  }),
});

export const useSort = createHook(ListPageStore, {
  selector: (state) => ({ sort: state.sort }),
});

export const useSearchTerm = createHook(ListPageStore, {
  selector: (state) => ({ searchTerm: state.searchTerm }),
});

export const useShowArchived = createHook(ListPageStore, {
  selector: (state) => ({ showArchived: state.showArchived }),
});

export const useCount = createHook(ListPageStore, {
  selector: (state) => ({ count: state.count }),
});

export const useColumns = createHook(ListPageStore, {
  selector: (state) => ({ cols: state.cols }),
});

export const useSelectedView = createHook(ListPageStore, {
  selector: (state) => ({ selectedView: state.selectedView }),
});

export const useSelectedViewColumns = createHook(ListPageStore, {
  selector: (state) => ({ selectedViewColumns: state.selectedViewColumns }),
});

export const useSkip = createHook(ListPageStore, {
  selector: (state) => ({ skip: state.skip }),
});

export const useTop = createHook(ListPageStore, {
  selector: (state) => ({ top: state.top }),
});

export const useAreAnyColumnsFiltered = createHook(ListPageStore, {
  selector: (state) => ({ areAnyColumnsFiltered: state.areAnyColumnsFiltered }),
});

export const useColumnOptionsSettings = createHook(ListPageStore, {
  selector: (state) => ({ columnOptionsSettings: state.columnOptionsSettings }),
});

export const useInvalidateOfferDialog = createHook(ListPageStore, {
  selector: (state) => ({ invalidateOfferDialog: state.invalidateOfferDialog }),
});

export const useAsiromFormDialog = createHook(ListPageStore, {
  selector: (state) => ({ asiromFormDialog: state.asiromFormDialog }),
});

export const useBcrFormDialog = createHook(ListPageStore, {
  selector: (state) => ({ bcrFormDialog: state.bcrFormDialog }),
});

export const useRenewalFormDialog = createHook(ListPageStore, {
  selector: (state) => ({ renewalFormDialog: state.renewalFormDialog }),
});

export const useRenewalNonGrecoFormDialog = createHook(ListPageStore, {
  selector: (state) => ({
    renewalNonGrecoFormDialog: state.renewalNonGrecoFormDialog,
  }),
});

export const useSearchState = createHook(ListPageStore, {
  selector: (state) => ({ searchState: state.searchState }),
});

export const useDebouncedState = createHook(ListPageStore, {
  selector: (state) => ({ debouncedState: state.debouncedState }),
});

export const useSelectedOfferRequestType = createHook(ListPageStore, {
  selector: (state) => ({
    selectedOfferRequestType: state.selectedOfferRequestType,
  }),
});
